// GAVION ai-email (spec §6 Demo/FakeRest): a deterministic draft built from the context, so the demo works with no API
// key. It never reads the client's message (nothing to inject) and states no figure. The brochure paragraph (the template's own link) only appears for an
// after_call whose context carries the brochure template (owner ruling D5). `variant` = « Régénérer ».
import type { FollowUpAngle } from "./followUp.ts";
import type { ContextInput, DraftOutput, DraftTrigger } from "./types.ts";

const esc = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const SUBJECT: Record<DraftTrigger, { fr: string; en: string }> = {
  after_call: { fr: "Suite à mon appel", en: "Following up on my call" },
  client_reply: {
    fr: "Suite à votre courriel",
    en: "Following up on your email",
  },
  after_demo: { fr: "Suite à notre démo", en: "Following up on our demo" },
  stale_lead: {
    fr: "Des nouvelles de votre côté ?",
    en: "Any news on your side?",
  },
  on_demand: { fr: "Suivi", en: "Follow-up" },
  follow_up: { fr: "Suivi", en: "Follow-up" },
};
const TOPIC: Record<DraftTrigger, { fr: string; en: string }> = {
  after_call: { fr: "notre appel", en: "our call" },
  client_reply: { fr: "votre message", en: "your message" },
  after_demo: { fr: "notre démo", en: "our demo" },
  stale_lead: { fr: "nos derniers échanges", en: "our last conversations" },
  on_demand: { fr: "nos échanges", en: "our conversations" },
  follow_up: { fr: "notre échange", en: "our conversation" },
};
const OPENERS = {
  fr: [
    "Je fais suite à",
    "Je reviens vers vous concernant",
    "Petit suivi concernant",
  ],
  en: [
    "I am following up on",
    "I am getting back to you about",
    "A quick follow-up on",
  ],
};

const ANGLE_LINE: Record<FollowUpAngle, { fr: string; en: string }> = {
  recap: {
    fr: "Voici un court résumé de notre échange et la prochaine étape.",
    en: "Here is a short recap of our conversation and the next step.",
  },
  value: {
    fr: "Gala répond à vos appels pendant que vous êtes sur un chantier et prend les rendez-vous à votre place.",
    en: "Gala answers your calls while you are on a job site and books the appointments for you.",
  },
  proof: {
    fr: "Concrètement, Gala répond, qualifie la demande et vous envoie un résumé, sans que vous ayez à décrocher.",
    en: "In practice, Gala answers, qualifies the request and sends you a summary, without you picking up.",
  },
  question: {
    fr: "Est-ce une mauvaise idée de regarder ensemble les appels que vous manquez ?",
    en: "Would it be a bad idea to look together at the calls you miss?",
  },
  pre_callback: {
    fr: "Je vous rappelle comme convenu ; si le moment ne convient plus, répondez-moi simplement avec celui qui vous va.",
    en: "I will call you back as agreed; if the time no longer works, just reply with one that does.",
  },
  breakup: {
    fr: "C'est mon dernier message à ce sujet : est-ce que je ferme votre dossier ?",
    en: "This is my last message on this: shall I close your file?",
  },
};

export function fakeDraft(input: ContextInput, variant = 0): DraftOutput {
  const lang = input.lead.language;
  const en = lang === "en";
  const contact =
    input.contacts.find((c) => c.id === input.recipient.contactId) ??
    input.contacts.find(
      (c) => c.email !== null && c.email === input.recipient.email,
    ) ??
    null;
  const first = contact?.firstName?.trim();
  const hello = first
    ? `${en ? "Hello" : "Bonjour"} ${esc(first)},`
    : en
      ? "Hello,"
      : "Bonjour,";
  const opener =
    OPENERS[lang][Math.abs(Math.trunc(variant)) % OPENERS[lang].length];
  const tpl = input.trigger === "after_call" ? input.brochureTemplate : null;
  const url = tpl?.url;
  const brochure =
    url && /^https:\/\//.test(url)
      ? `<p>${en ? "Here is our brochure:" : "Voici notre brochure :"} <a href="${esc(url)}">${en ? "Gavion brochure" : "brochure Gavion"}</a></p>`
      : "";
  const ask = en
    ? "Would you have 15 minutes this week to talk about it?"
    : "Auriez-vous 15 minutes cette semaine pour en discuter ?";
  // GAVION ai follow-up: a touch gets the text of its angle; breakup and pre_callback carry no extra ask
  const fu = input.trigger === "follow_up" ? (input.followUp ?? null) : null;
  if (fu) {
    const detail = fu.websiteDetail?.trim();
    const site = detail
      ? `<p>${en ? "I looked at your website:" : "J'ai regardé votre site :"} ${esc(detail)}.</p>`
      : "";
    const close =
      fu.angle === "breakup" ||
      fu.angle === "pre_callback" ||
      fu.angle === "question"
        ? ""
        : `<p>${ask}</p>`;
    return {
      subject: `${SUBJECT.follow_up[lang]} ${fu.position}/${fu.total} — ${input.lead.name}`,
      body: `<p>${hello}</p>${site}<p>${ANGLE_LINE[fu.angle][lang]}</p>${close}`,
      language: lang,
      rationale: `(démo) Touche ${fu.position} de ${fu.total}, angle ${fu.angle}.`,
      angle: fu.angle,
    };
  }
  const instruction = input.instruction
    ? ` (consigne : ${input.instruction.trim().slice(0, 80)})`
    : "";
  return {
    subject:
      tpl?.subject.trim() ||
      `${SUBJECT[input.trigger][lang]} — ${input.lead.name}`,
    body: `<p>${hello}</p><p>${opener} ${TOPIC[input.trigger][lang]}.</p>${brochure}<p>${ask}</p>`,
    language: lang,
    rationale: `(démo) Relance après ${TOPIC[input.trigger].fr} ; propose un appel de 15 min${instruction}.`,
  };
}
